package com.arena_web.ui.sections.eventdetail

import androidx.compose.runtime.*
import com.arena_web.data.dto.EventDetailDto
import com.arena_web.data.dto.EventParticipantDto
import com.arena_web.ui.components.cards.PlayerCard
import com.arena_web.ui.components.cards.PlayerCardSize
import com.arena_web.ui.components.icons.Icon
import com.arena_web.ui.localization.LocalAppStrings
import com.arena_web.ui.theme.*
import com.arena_web.ui.utils.*
import com.arena_web.utils.Storage
import com.arena_web.utils.StorageKeys
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.css.Overflow
import com.varabyte.kobweb.compose.foundation.layout.*
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Color
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.attrsModifier
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.silk.components.graphics.Image
import com.varabyte.kobweb.silk.components.text.SpanText
import kotlinx.browser.window
import org.jetbrains.compose.web.css.*

@Composable
fun PlayersGrid(
    event: EventDetailDto,
    isAdmin: Boolean,
    onAddPlayerClick: () -> Unit = {},
    onConfirmPlayer: (String) -> Unit = {},
    onRejectPlayer: (String) -> Unit = {},
    onRemoveFromEvent: (userId: String) -> Unit = {},
    onToggleAdmin: (userId: String, makeAdmin: Boolean) -> Unit = { _, _ -> },
    onAssignToTeam: (userId: String, eventTeamId: String) -> Unit = { _, _ -> },
    onViewProfile: (userId: String) -> Unit = {},
    modifier: Modifier = Modifier
) {
    // Show every participant — team-assigned players surface their team via
    // the top-left badge on the card, so this grid stays the single source
    // of truth for the roster.
    val confirmed = event.participants.filter { it.status.uppercase() == "CONFIRMED" }
    val pending = event.participants.filter { it.status.uppercase() == "PENDING" }

    // Paginated single-row block: the area never grows; when more than a page
    // fits, the side arrows page through. Cards are sized to the fixed block.
    val roster = confirmed + pending
    val cols = 6
    val pageSize = cols // one row per page
    val pages = ((roster.size + pageSize - 1) / pageSize).coerceAtLeast(1)
    var page by remember(roster.size) { mutableStateOf(0) }

    // Warm the image cache for every page up-front so the first flip is instant.
    LaunchedEffect(roster.size) { com.arena_web.ui.components.cards.preloadPlayerCardAssets(roster) }

    // Pre-paint warm-up: render every page stacked ON-screen for a couple frames
    // so the browser rasterises them, then spread them to their slide positions.
    var spread by remember { mutableStateOf(false) }
    var slideEnabled by remember { mutableStateOf(false) }
    LaunchedEffect(pages) {
        spread = false; slideEnabled = false
        window.requestAnimationFrame {
            window.requestAnimationFrame {
                spread = true
                window.requestAnimationFrame { slideEnabled = true }
            }
        }
    }

    Column(modifier = modifier.styleModifier { property("align-self", "stretch") }) {
        // Framed container — fills remaining height. The bottom-bracket SVG
        // (`players-frame-bottom.svg`, U-shape with the dip pointing UP) is
        // rotated 90° and stretched to the panel's full height on the LEFT
        // side, then mirrored to the RIGHT, so both edges read as vertical
        // brackets whose dip points INTO the player grid.
        Box(
            modifier = Modifier
                .fillMaxWidth()
                .flexGrow(1)
                .minHeight(0.px)
                .position(Position.Relative),
        ) {
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .fillMaxHeight(),
            verticalAlignment = Alignment.CenterVertically,
        ) {
            SideBracketDecoration(side = SideBracket.Left)

            // Scrollable cards area with the Figma 7069:803308 "Chat
            // Container" SVG rotated 90° so its two gradient strokes — which
            // sit on the LEFT and RIGHT of the SVG's viewBox — render as
            // HORIZONTAL lines along the top and bottom of the players grid.
            // `container-type: size` on the wrapper lets the rotated SVG use
            // cqw/cqh to swap its pre-rotation width and height to match the
            // panel without knowing them upfront.
            Box(
                modifier = Modifier
                    .fillMaxHeight()
                    .flexGrow(1)
                    .minHeight(0.px)
                    .position(Position.Relative),
            ) {
                Box(
                    modifier = Modifier
                        .position(Position.Absolute)
                        .top(0.px).left(0.px).right(0.px).bottom(0.px)
                        .fillMaxSize()
                        .styleModifier {
                            property("container-type", "size")
                            property("pointer-events", "none")
                            property("z-index", "0")
                            property("overflow", "hidden")
                        }
                ) {
                    Image(
                        src = "/images/event-chat-container.svg",
                        modifier = Modifier.styleModifier {
                            property("position", "absolute")
                            property("top", "0")
                            property("left", "0")
                            property("width", "100cqh")
                            property("height", "100cqw")
                            property("pointer-events", "none")
                            property("transform-origin", "0 0")
                            property("transform", "translateX(100cqw) rotate(90deg)")
                        }
                    )
                }
                // Paginated 2-row grid. `container-type: size` makes cqw/cqh the
                // block's own size so each card can shrink to fit a fixed 2-row
                // layout (no growth, no scroll).
                val hSub = 56 + (cols - 1) * 20          // h-padding (28×2) + col gaps
                Box(
                    modifier = Modifier
                        .fillMaxSize()
                        .position(Position.Relative)
                        .styleModifier {
                            property("z-index", "1")
                            // Sizing reference for the cards; clips the off-edge page.
                            property("container-type", "size")
                            property("overflow", "hidden")
                        }
                ) {
                    // Horizontal pager — pages absolutely stacked. A warm-up
                    // paints them all at translateX(0), then spreads them to their
                    // slide positions, so flipping reuses the cached layer raster.
                    for (p in 0 until pages) {
                            val pageItems = roster.drop(p * pageSize).take(pageSize)
                            val rowsOnPage = ((pageItems.size + cols - 1) / cols).coerceIn(1, 2)
                            val vSub = 80 + (rowsOnPage - 1) * 20    // v-padding (40×2) + row gaps
                            Column(
                                modifier = Modifier
                                    .position(Position.Absolute)
                                    .styleModifier {
                                        property("top", "0"); property("left", "0")
                                        property("right", "0"); property("bottom", "0")
                                        property("will-change", "transform")
                                        property("transform", if (spread) "translateX(${(p - page) * 100.0}%)" else "translateX(0)")
                                        if (slideEnabled) property("transition", "transform 0.3s ease")
                                        property("opacity", if (spread || p == page) "1" else "0")
                                        property("z-index", if (p == page) "2" else "1")
                                        property("justify-content", "center")
                                        property("gap", s(20))
                                        property("padding", "${s(40)} ${s(28)}")
                                        property("box-sizing", "border-box")
                                    }
                            ) {
                                pageItems.chunked(cols).forEach { rowItems ->
                                    Row(
                                        modifier = Modifier
                                            .fillMaxWidth()
                                            .styleModifier {
                                                // Left-align so a partial row fills from
                                                // the start instead of bunching centre.
                                                property("justify-content", "flex-start")
                                                property("gap", s(20))
                                            }
                                    ) {
                                        rowItems.forEach { participant ->
                                            val pendingCard = participant.status.uppercase() == "PENDING"
                                            PlayerSlotCard(
                                                participant = participant,
                                                event = event,
                                                isPending = pendingCard,
                                                isAdmin = isAdmin,
                                                onConfirm = { onConfirmPlayer(participant.userId) },
                                                onReject = { onRejectPlayer(participant.userId) },
                                                onRemoveFromEvent = { onRemoveFromEvent(participant.userId) },
                                                onToggleAdmin = { onToggleAdmin(participant.userId, !participant.isAdmin) },
                                                onAssignToTeam = { eventTeamId -> onAssignToTeam(participant.userId, eventTeamId) },
                                                onViewProfile = { onViewProfile(participant.userId) },
                                                slotModifier = Modifier.styleModifier {
                                                    property(
                                                        "width",
                                                        "min(calc((100cqw - ${s(hSub)}) / $cols), calc((100cqh - ${s(vSub)}) / $rowsOnPage * 91 / 142))",
                                                    )
                                                    property("aspect-ratio", "91 / 142")
                                                    property("flex-shrink", "0")
                                                },
                                                fillCard = true,
                                            )
                                        }
                                    }
                                }
                            }
                    }
                }
            }

            SideBracketDecoration(side = SideBracket.Right)
        }

            // Pagination arrows — hug the outer sides of the whole block,
            // vertically centred. The icon sits flush at the edge (no inset),
            // so the chevron hugs the side rather than floating centred in its
            // 44px hit area. Hidden entirely at the ends (no dimmed state).
            if (page > 0) {
                Box(
                    modifier = Modifier
                        .position(Position.Absolute)
                        .zIndex(3)
                        .sSize(44)
                        .cursor(Cursor.Pointer)
                        .onClick { page-- }
                        .styleModifier {
                            property("left", "0"); property("top", "50%")
                            property("transform", "translateY(-50%)")
                        },
                    // Icon hugs the outer (left) edge of the 44px hit area.
                    contentAlignment = Alignment.CenterStart,
                ) { Icon(src = AppIcons.ChevronBack, modifier = Modifier.sSize(28)) }
            }
            if (page < pages - 1) {
                Box(
                    modifier = Modifier
                        .position(Position.Absolute)
                        .zIndex(3)
                        .sSize(44)
                        .cursor(Cursor.Pointer)
                        .onClick { page++ }
                        .styleModifier {
                            property("right", "0"); property("top", "50%")
                            property("transform", "translateY(-50%)")
                        },
                    // Icon hugs the outer (right) edge of the 44px hit area.
                    contentAlignment = Alignment.CenterEnd,
                ) {
                    Icon(
                        src = AppIcons.ChevronBack,
                        modifier = Modifier.sSize(28).styleModifier { property("transform", "rotate(180deg)") },
                    )
                }
            }
        }
    }
}

/**
 * Walks up from [start] to the nearest ancestor that establishes a containing
 * block for `position: fixed` descendants — i.e. one with a non-`none`
 * `transform`, `filter`, `perspective`, or a `will-change` naming any of them.
 * Returns null when none exists (so `fixed` resolves against the viewport).
 */
private fun fixedContainingBlock(start: org.w3c.dom.HTMLElement?): org.w3c.dom.HTMLElement? {
    var el = start?.parentElement
    while (el != null) {
        (el as? org.w3c.dom.HTMLElement)?.let { he ->
            val cs = window.getComputedStyle(he)
            val transform = cs.transform
            val willChange = cs.getPropertyValue("will-change")
            val filter = cs.getPropertyValue("filter")
            val perspective = cs.getPropertyValue("perspective")
            val establishes = (transform.isNotEmpty() && transform != "none") ||
                (filter.isNotEmpty() && filter != "none") ||
                (perspective.isNotEmpty() && perspective != "none") ||
                willChange.contains("transform") || willChange.contains("filter") ||
                willChange.contains("perspective")
            if (establishes) return he
        }
        el = el.parentElement
    }
    return null
}

internal enum class SideBracket { Left, Right }

/**
 * Vertical bracket decoration on the left/right edge of the players grid —
 * uses `players-frame-bottom.svg` rotated 90° CW (so its upward dip points
 * RIGHT, toward content). The right-side instance mirrors the container so
 * the dip points LEFT instead. `container-type: size` + `width: 100cqh`
 * makes the rotated SVG span the parent's full height without knowing it
 * upfront.
 */
@Composable
internal fun SideBracketDecoration(side: SideBracket) {
    // overflow:visible + a 1.5px outset on the rotated SVG so the stroke
    // (1.5px wide, centred on the path) and the path's 0.75px viewBox
    // margins on both ends fall back inside the container's visible area
    // instead of getting clipped at the top/right/bottom corners.
    Box(
        modifier = Modifier
            .fillMaxHeight()
            .sWidth(30)
            .position(Position.Relative)
            .styleModifier {
                property("flex-shrink", "0")
                property("container-type", "size")
                property("overflow", "visible")
                if (side == SideBracket.Right) {
                    property("transform", "scaleX(-1)")
                }
            }
    ) {
        Image(
            src = "/images/players-frame-bottom.svg",
            modifier = Modifier.styleModifier {
                property("position", "absolute")
                property("top", "-1.5px")
                property("left", "-1.5px")
                property("width", "calc(100cqh + 3px)")
                property("height", "calc(30px + 3px)")
                property("pointer-events", "none")
                property("transform-origin", "0 0")
                property("transform", "translateX(30px) rotate(90deg)")
            }
        )
    }
}

@Composable
private fun AddPlayerCard(onClick: () -> Unit) {
    val sportColors = LocalSportColors.current

    Box(
        modifier = Modifier
            .sWidth(110)
            .sHeight(170)
            .sBorderRadius(12)
            .cursor(Cursor.Pointer)
            .onClick { onClick() }
            .overflow(Overflow.Hidden)
            .position(Position.Relative)
            .styleModifier {
                property(
                    "background",
                    "linear-gradient(180deg, rgba(0,0,0,0) 0%, rgba(48,108,206,0.2) 100%)"
                )
                property("border", "0.4px solid rgba(255,255,255,0.3)")
            }
    ) {
        // Player silhouette
        Box(
            modifier = Modifier
                .position(Position.Absolute)
                .styleModifier {
                    property("left", "50%")
                    property("top", s(26))
                    property("transform", "translateX(-50%)")
                    property("width", s(55))
                    property("height", s(97))
                    property("border-bottom-left-radius", s(110))
                    property("border-bottom-right-radius", s(110))
                    property("overflow", "hidden")
                }
        ) {
            Image(
                src = "/images/player-silhouette.png",
                modifier = Modifier
                    .position(Position.Absolute)
                    .styleModifier {
                        property("width", "136%")
                        property("height", "117%")
                        property("left", "-20%")
                        property("top", "-6%")
                        property("max-width", "none")
                        property("opacity", "0.55")
                        property("filter", "brightness(1.2) sepia(1) saturate(3) hue-rotate(200deg)")
                    }
            )
        }

        // Blue + button at bottom
        Box(
            modifier = Modifier
                .position(Position.Absolute)
                .styleModifier {
                    property("bottom", s(24))
                    property("left", "50%")
                    property("transform", "translateX(-50%)")
                }
        ) {
            Box(
                modifier = Modifier
                    .sSize(32)
                    .sBorderRadius(100)
                    .backgroundColor(sportColors.primary),
                contentAlignment = Alignment.Center
            ) {
                Icon(
                    src = AppIcons.Add,
                    modifier = Modifier.sSize(16).styleModifier {
                        property("filter", sportColors.iconFilterOnPrimary)
                    }
                )
            }
        }
    }
}

@Composable
private fun PlayerSlotCard(
    participant: EventParticipantDto,
    event: EventDetailDto,
    isPending: Boolean,
    isAdmin: Boolean = false,
    onConfirm: () -> Unit = {},
    onReject: () -> Unit = {},
    onRemoveFromEvent: () -> Unit = {},
    onToggleAdmin: () -> Unit = {},
    onAssignToTeam: (eventTeamId: String) -> Unit = {},
    onViewProfile: () -> Unit = {},
    // Outer slot sizing. Default = the original fixed 180×284; the paginated
    // grid passes a responsive width + aspect-ratio so cards shrink to fit.
    slotModifier: Modifier = Modifier.sWidth(180).sHeight(284),
    // When true the FIFA card fills the slot (responsive) instead of the fixed
    // `scale(1.7)` blow-up.
    fillCard: Boolean = false,
) {
    val t = LocalAppStrings.current
    val currentUserId = Storage.get(StorageKeys.USER_ID)
    val isClickable = participant.userId != currentUserId
    val displayName = (participant.fullName?.split(" ")?.lastOrNull() ?: participant.username).uppercase()

    // Slot follows Figma 7802:312843 styling (12px radius, faint blue wash,
    // 0.5px white edge).
    val isPendingAdmin = isPending && isAdmin
    Box(
        modifier = Modifier
            .then(slotModifier)
            .sBorderRadius(12)
            .position(Position.Relative)
            .styleModifier {
                property(
                    "background",
                    "linear-gradient(180deg, rgba(0,0,0,0) 0%, rgba(48,108,206,0.13) 100%)"
                )
                property("box-sizing", "border-box")
                property("overflow", "visible")
            }
    ) {
        // Solid hairline ring — the previous mask-composite gradient border
        // rendered inconsistently at its 0.3px sub-pixel width on the
        // responsive/animated slot (visible flicker). A flat faint-white edge
        // looks the same but never flickers.
        Box(
            modifier = Modifier.styleModifier {
                property("position", "absolute")
                property("inset", "0")
                property("border", "1px solid rgba(255, 255, 255, 0.12)")
                property("border-radius", s(12))
                property("pointer-events", "none")
                property("z-index", "1")
            }
        )
        // Player card sits in the slot with Figma's 6px horizontal / 4px
        // vertical padding. Bottom padding grows for pending+admin to free
        // up room for the action buttons that sit below the card.
        Box(
            modifier = Modifier
                .fillMaxSize()
                .sPaddingTop(if (fillCard) 10 else 16)
                .sPaddingBottom(if (fillCard) 8 else 12)
                .sPaddingLeft(if (fillCard) 5 else 6)
                .sPaddingRight(if (fillCard) 5 else 6)
                .styleModifier { property("box-sizing", "border-box") },
            contentAlignment = Alignment.TopCenter
        ) {
            if (participant.cardAssetsBasePath != null) {
                PlayerCard(
                    playerName = displayName,
                    rating = participant.overallRating.toInt(),
                    position = participant.position ?: "",
                    sport = event.sportName,
                    size = PlayerCardSize.Arena,
                    avatarUrl = participant.cardAvatarUrl ?: AppIcons.EmptyAvatar,
                    cardAssetsBasePath = participant.cardAssetsBasePath,
                    cardTextColor = participant.cardTextColor,
                    fillContainer = fillCard,
                    // Desktop runs at --scale ≠ 1, so the fit ratio must be
                    // scale-aware to actually fill the responsive slot.
                    fillContainerScaleAware = fillCard,
                    modifier = Modifier
                        .then(if (isClickable) Modifier.cursor(Cursor.Pointer).onClick { onViewProfile() } else Modifier)
                        .styleModifier {
                            if (!fillCard) {
                                property("transform", "scale(1.7)")
                                property("transform-origin", "top center")
                            }
                        }
                )
            } else {
                // Fallback without card frame — avatar + name
                Column(
                    modifier = Modifier
                        .sGap(6)
                        .then(if (isClickable) Modifier.cursor(Cursor.Pointer).onClick { onViewProfile() } else Modifier),
                    horizontalAlignment = Alignment.CenterHorizontally
                ) {
                    Box(
                        modifier = Modifier
                            .sSize(52)
                            .sBorderRadius(100)
                            .overflow(Overflow.Hidden)
                            .styleModifier { property("background", "rgba(255,255,255,0.1)") },
                        contentAlignment = Alignment.Center
                    ) {
                        Image(
                            src = participant.avatarUrl ?: AppIcons.EmptyAvatar,
                            modifier = Modifier.fillMaxSize()
                                .styleModifier { property("object-fit", "cover") }
                        )
                    }
                    SpanText(
                        displayName,
                        modifier = Modifier
                            .sFontSize(11)
                            .fontWeight(FontWeight.Medium)
                            .color(AppColors.PrimaryWhite)
                            .styleModifier {
                                property("white-space", "nowrap")
                                property("overflow", "hidden")
                                property("text-overflow", "ellipsis")
                                property("max-width", s(90))
                            }
                    )
                }
            }
        }

        // Top-left badge: team-colour swatch when the player is assigned to
        // a squad, verified check for unassigned-confirmed, pulsing amber
        // dot for pending — all anchored to the same corner so the card art
        // doesn't shift between states.
        if (!isPending) {
            val assignedTeam = participant.eventTeamId?.let { tid ->
                event.teams.find { it.id == tid }
            }
            Box(
                modifier = Modifier
                    .position(Position.Absolute)
                    .zIndex(2)
                    .styleModifier {
                        property("top", s(6))
                        property("left", s(6))
                    }
            ) {
                if (assignedTeam != null) {
                    TeamColorBadge(color = assignedTeam.color ?: "#0D184A", size = 30)
                } else {
                    Image(
                        src = AppIcons.VerifiedBadge,
                        modifier = Modifier.sSize(30)
                    )
                }
            }
        } else {
            Box(
                modifier = Modifier
                    .position(Position.Absolute)
                    .sSize(14)
                    .borderRadius(50.percent)
                    .styleModifier {
                        property("top", s(12))
                        property("left", s(12))
                        property("background-color", "#FFB800")
                        property("box-shadow", "0 0 8px rgba(255, 184, 0, 0.7)")
                        property("animation", "pp-pending-dot 1.4s ease-in-out infinite")
                    }
            )
            androidx.compose.runtime.LaunchedEffect(Unit) {
                val styleId = "pp-pending-dot-style"
                if (kotlinx.browser.document.getElementById(styleId) == null) {
                    val styleEl = kotlinx.browser.document.createElement("style")
                    styleEl.id = styleId
                    styleEl.textContent = """
                        @keyframes pp-pending-dot {
                          0%, 100% { opacity: 1;   transform: scale(1); }
                          50%      { opacity: 0.4; transform: scale(0.8); }
                        }
                    """.trimIndent()
                    kotlinx.browser.document.head?.appendChild(styleEl)
                }
            }
        }

        // Ellipsis menu — top-right (admin only, confirmed players). Shown on
        // self when at least one team exists so the admin can drop themselves
        // into a squad or move between teams. Toggle-Admin / Remove rows are
        // hidden on self further down.
        val isSelf = participant.userId == currentUserId
        val canSelfAssignTeam = isSelf && event.teams.isNotEmpty()
        if (!isPending && isAdmin && (!isSelf || canSelfAssignTeam)) {
            var showMenu by remember { mutableStateOf(false) }
            var menuTop by remember { mutableStateOf(0.0) }
            var menuRight by remember { mutableStateOf(0.0) }
            // The pager pages carry `transform`/`will-change: transform`, which
            // makes a `position: fixed` descendant resolve against THAT element
            // instead of the viewport. We find that containing block and express
            // the menu/scrim coords relative to it (origin = its top-left).
            var cbLeft by remember { mutableStateOf(0.0) }
            var cbTop by remember { mutableStateOf(0.0) }

            Box(
                modifier = Modifier
                    .position(Position.Absolute)
                    .zIndex(5)
                    .styleModifier {
                        property("top", s(8))
                        property("right", s(8))
                    }
            ) {
                Box(
                    modifier = Modifier
                        .sSize(22)
                        .cursor(Cursor.Pointer)
                        .attrsModifier {
                            onClick { evt ->
                                evt.stopPropagation()
                                val el = evt.nativeEvent.currentTarget as? org.w3c.dom.HTMLElement
                                val rect = el?.getBoundingClientRect()
                                if (rect != null) {
                                    // Resolve coords against the fixed containing
                                    // block (a transformed pager page) if any, else
                                    // the viewport.
                                    val cb = fixedContainingBlock(el)
                                    val cbRect = cb?.getBoundingClientRect()
                                    cbLeft = cbRect?.left ?: 0.0
                                    cbTop = cbRect?.top ?: 0.0
                                    val originTop = cbRect?.top ?: 0.0
                                    val originRight = cbRect?.right ?: window.innerWidth.toDouble()
                                    menuTop = rect.bottom - originTop + 4.0
                                    menuRight = originRight - rect.right
                                }
                                showMenu = !showMenu
                            }
                        },
                    contentAlignment = Alignment.Center
                ) {
                    Icon(
                        src = AppIcons.EllipsisVertical,
                        modifier = Modifier.sSize(18)
                    )
                }
            }

            if (showMenu) {
                // Full-viewport click-catcher. Offset by the containing block's
                // top-left (and sized in vw/vh, which ignore the containing block)
                // so it covers the whole window even when fixed resolves against a
                // transformed pager page.
                Box(
                    modifier = Modifier
                        .position(Position.Fixed)
                        .zIndex(10)
                        .onClick { showMenu = false }
                        .styleModifier {
                            property("top", "${-cbTop}px")
                            property("left", "${-cbLeft}px")
                            property("width", "100vw")
                            property("height", "100vh")
                        }
                )
                Column(
                    modifier = Modifier
                        .position(Position.Fixed)
                        .zIndex(11)
                        .sMinWidth(160)
                        .sBorderRadius(10)
                        .overflow(Overflow.Hidden)
                        .styleModifier {
                            property("top", "${menuTop}px")
                            property("right", "${menuRight}px")
                            property("background", "rgba(20, 24, 32, 0.95)")
                            property("backdrop-filter", "blur(16px)")
                            property("border", "1px solid rgba(255, 255, 255, 0.08)")
                            property("box-shadow", "0 8px 24px rgba(0, 0, 0, 0.5)")
                        }
                ) {
                    // Add to Team — shown whenever any teams exist. When the
                    // player is already on a team the current one is highlighted
                    // in the submenu so the admin can re-assign in one click.
                    if (event.teams.isNotEmpty()) {
                        var showTeamSubmenu by remember { mutableStateOf(false) }

                        Column(modifier = Modifier.fillMaxWidth()) {
                            Row(
                                modifier = Modifier
                                    .fillMaxWidth()
                                    .cursor(Cursor.Pointer)
                                    .sPadding(10, 14)
                                    .onClick { showTeamSubmenu = !showTeamSubmenu }
                                    .sGap(8),
                                verticalAlignment = Alignment.CenterVertically
                            ) {
                                Icon(
                                    src = AppIcons.People,
                                    modifier = Modifier.sSize(16).styleModifier {
                                        property("filter", "brightness(0) invert(1)")
                                    }
                                )
                                SpanText(
                                    t.eventAddToTeam,
                                    modifier = Modifier
                                        .sFontSize(13)
                                        .fontWeight(FontWeight.Medium)
                                        .color(AppColors.PrimaryWhite)
                                        .flexGrow(1)
                                )
                                Icon(
                                    src = AppIcons.ChevronForward,
                                    modifier = Modifier.sSize(12).styleModifier {
                                        property("filter", "brightness(0) invert(1)")
                                        if (showTeamSubmenu) property("transform", "rotate(90deg)")
                                    }
                                )
                            }

                            if (showTeamSubmenu) {
                                event.teams.forEach { team ->
                                    val isCurrentTeam = participant.eventTeamId == team.id
                                    Row(
                                        modifier = Modifier
                                            .fillMaxWidth()
                                            .cursor(Cursor.Pointer)
                                            .sPadding(10, 14)
                                            .sPaddingLeft(38)
                                            .onClick {
                                                showMenu = false
                                                onAssignToTeam(team.id)
                                            }
                                            .sGap(10)
                                            .then(
                                                if (isCurrentTeam) Modifier.styleModifier {
                                                    property("background", "rgba(0, 97, 255, 0.16)")
                                                } else Modifier
                                            ),
                                        verticalAlignment = Alignment.CenterVertically
                                    ) {
                                        // Same textured team-card swatch the players list uses
                                        // (TeamColorPills / participant badge), shrunk so it reads
                                        // as a glyph next to the team name.
                                        TeamColorBadge(color = team.color ?: "#0D184A", size = 24)
                                        SpanText(
                                            team.name,
                                            modifier = Modifier
                                                .sFontSize(14)
                                                .fontWeight(FontWeight.SemiBold)
                                                .color(AppColors.PrimaryWhite)
                                                .flexGrow(1)
                                        )
                                        if (isCurrentTeam) {
                                            Icon(
                                                src = AppIcons.Check,
                                                modifier = Modifier.sSize(16),
                                            )
                                        }
                                    }
                                }
                            }
                        }
                    }

                    // Toggle Admin / Remove are admin-on-someone-else actions —
                    // don't let admins demote or eject themselves from here.
                    if (!isSelf) {
                        // Toggle Admin
                        Row(
                            modifier = Modifier
                                .fillMaxWidth()
                                .cursor(Cursor.Pointer)
                                .sPadding(10, 14)
                                .onClick {
                                    showMenu = false
                                    onToggleAdmin()
                                }
                                .sGap(8),
                            verticalAlignment = Alignment.CenterVertically
                        ) {
                            Icon(
                                src = AppIcons.Shield,
                                modifier = Modifier.sSize(16).styleModifier {
                                    property("filter", "brightness(0) invert(1)")
                                }
                            )
                            SpanText(
                                if (participant.isAdmin) t.eventRemoveAdmin else t.eventMakeAdmin,
                                modifier = Modifier
                                    .sFontSize(13)
                                    .fontWeight(FontWeight.Medium)
                                    .color(AppColors.PrimaryWhite)
                            )
                        }

                        // Remove from Event
                        Row(
                            modifier = Modifier
                                .fillMaxWidth()
                                .cursor(Cursor.Pointer)
                                .sPadding(10, 14)
                                .onClick {
                                    showMenu = false
                                    onRemoveFromEvent()
                                }
                                .sGap(8),
                            verticalAlignment = Alignment.CenterVertically
                        ) {
                            Box(
                                modifier = Modifier.sSize(16).styleModifier {
                                    property("background-color", "#FF5050")
                                    property("-webkit-mask", "url('${AppIcons.Trash}') center / contain no-repeat")
                                    property("mask", "url('${AppIcons.Trash}') center / contain no-repeat")
                                }
                            )
                            SpanText(
                                t.eventRemoveFromEvent,
                                modifier = Modifier
                                    .sFontSize(13)
                                    .fontWeight(FontWeight.Medium)
                                    .color(Color.rgb(255, 80, 80))
                            )
                        }
                    }
                }
            }
        }

        // Confirm/Reject buttons for pending players (admin only) — Figma
        // Action buttons live in the bottom corners (reject left, confirm
        // right) so they sit over the card art instead of competing with
        // the central rating column. Slot height stays the same as the
        // confirmed variant — the buttons overlay the card's lower corners.
        if (isPending && isAdmin) {
            Row(
                modifier = Modifier
                    .position(Position.Absolute)
                    .sGap(8)
                    .zIndex(2)
                    .styleModifier {
                        property("bottom", s(0))
                        property("left", "50%")
                        property("transform", "translateX(-50%)")
                    },
                verticalAlignment = Alignment.CenterVertically,
            ) {
                ActionPillButton(
                    symbolIcon = AppIcons.CrossMark,
                    pillColor = "#FF4242",
                    rotateDeg = 2,
                    onClick = onReject,
                )
                ActionPillButton(
                    symbolIcon = AppIcons.CheckMark,
                    pillColor = "#7BFA6F",
                    rotateDeg = -2,
                    onClick = onConfirm,
                )
            }
        }

    }
}

/**
 * 40×40 hit target wrapping the 32×14 colour pill icon (BtnReject /
 * BtnConfirm). Matches the Figma "Action Buttons" sub-component from
 * 7802:300305 — the touch area is larger than the visual so the buttons
 * are comfortable to tap without enlarging the pill itself.
 */
@Composable
private fun ActionPillButton(
    symbolIcon: String,
    pillColor: String,
    rotateDeg: Int = 0,
    onClick: () -> Unit,
) {
    // Pill is drawn via CSS so its width can grow independently; the
    // symbol stays at its natural aspect (no stretch), centered inside.
    Box(
        modifier = Modifier
            .sWidth(72)
            .sHeight(40)
            .cursor(Cursor.Pointer)
            .onClick {
                it.stopPropagation()
                onClick()
            },
        contentAlignment = Alignment.Center
    ) {
        Box(
            modifier = Modifier
                .sWidth(64)
                .sHeight(18)
                .sBorderRadius(9)
                .styleModifier {
                    property("background-color", pillColor)
                    if (rotateDeg != 0) {
                        property("transform", "rotate(${rotateDeg}deg)")
                        property("transform-origin", "center")
                    }
                },
            contentAlignment = Alignment.Center
        ) {
            Image(
                src = symbolIcon,
                modifier = Modifier.sSize(12)
            )
        }
    }
}

